/**
 * RelatedQuestionsView — the related-questions list as it appears in VIEW mode,
 * inside "More Details".
 *
 * Deliberately not the edit-mode `RelatedQuestionsSection` with its inputs
 * disabled. That row expands to a form — question-id field, relation-type
 * select, reason box — which is authoring machinery. A reader opening this
 * wants to know *which* questions are related and *how*, then go read one; the
 * id and the raw relation value tell them nothing they can act on here.
 *
 * So the row carries three things and no chevron:
 *   - the relation, as its human label ("AI-Generated Variation")
 *   - the stem, truncated, whose trailing "…" expands it in place
 *   - a link that opens the question in a new tab, leaving this one open
 *
 * The stem is not on the question document — `relatedQuestions` stores ids —
 * so it is fetched per id and cached. A fetch that fails leaves the row
 * showing its id rather than dropping it: a related question whose target was
 * deleted is exactly the case someone needs to see.
 */
import { useEffect, useRef, useState } from 'react';
import { LatexText } from '../latex';
import { RELATION_TYPE_LABELS } from '../question-set-types';
import type { RelatedQuestion } from '../question-edit-form/formTypes';

export interface RelatedQuestionsViewProps {
    relatedQuestions?: RelatedQuestion[] | null;
    /** Resolves a related question's stem. Omitted ⇒ rows show ids only. */
    fetchQuestionText?: (id: string) => Promise<string>;
    /** Builds the URL the row links to. Omitted ⇒ the stem renders unlinked. */
    questionHref?: (id: string) => string;
}

/** How much stem a collapsed row shows before the expandable "…". */
const STEM_PREVIEW_CHARS = 160;

export function RelatedQuestionsView({
    relatedQuestions, fetchQuestionText, questionHref,
}: RelatedQuestionsViewProps) {
    const items = relatedQuestions ?? [];
    const [textCache, setTextCache] = useState<Record<string, string>>({});

    // Depend on the joined ids, not the array identity: the parent rebuilds
    // `relatedQuestions` on every render, and an array dep would refetch every
    // stem each time.
    const idKey = items.map(rq => rq.questionId).join(',');

    // Same reason, for the fetcher: consumers build the page's `api` object
    // inline, so its identity changes every render. Held in a ref so the effect
    // keys on the ids alone and cannot re-fire on an unrelated re-render.
    const fetchRef = useRef(fetchQuestionText);
    fetchRef.current = fetchQuestionText;

    useEffect(() => {
        const fetchText = fetchRef.current;
        if (!fetchText) return;
        const ids = items.map(rq => rq.questionId).filter(id => id && !textCache[id]);
        if (ids.length === 0) return;
        let cancelled = false;
        Promise.all(ids.map(id =>
            fetchText(id)
                .then(text => ({ id, text }))
                .catch(() => ({ id, text: '' })),
        )).then(results => {
            if (cancelled) return;
            setTextCache(prev => {
                const next = { ...prev };
                for (const r of results) if (r.text) next[r.id] = r.text;
                return next;
            });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idKey]);

    if (items.length === 0) return null;

    return (
        <div className="view-section">
            <h3>Related Questions ({items.length})</h3>
            <div className="rqv-list">
                {items.map((rq, idx) => {
                    const text = textCache[rq.questionId];
                    const href = questionHref?.(rq.questionId);
                    return (
                        <div className="rqv-item" key={`${rq.questionId}-${idx}`}>
                            <span className="rqv-relation">
                                {RELATION_TYPE_LABELS[rq.relationType] || rq.relationType}
                            </span>
                            <span className="rqv-stem">
                                {text ? (
                                    // expandInline turns the trailing "…" into the control that
                                    // reveals the rest, so a long stem costs a click, not a trip
                                    // to another tab.
                                    <LatexText text={text} truncate={STEM_PREVIEW_CHARS} expandInline />
                                ) : (
                                    <code className="rqv-id" title={rq.questionId}>{rq.questionId}</code>
                                )}
                            </span>
                            {href && (
                                <a
                                    className="rqv-open"
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open this question in a new tab"
                                >
                                    Open ↗
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
